import {
  getGetInvitationsApiV1QueryKey,
  getGetWorkspacesApiV1QueryKey,
  useAcceptInvitationApiV1,
  useDeclineInvitationApiV1,
  useGetInvitationApiV1Suspense,
  useGetInvitationsApiV1Suspense,
} from "@/api/__generated__/api.schema";
import type { WorkspaceInvitation } from "@/api/__generated__/models";
import { AppSidebar } from "@/components/app/app-sidebar";
import { GlobalProviders } from "@/components/common/global-providers";
import { WorkspaceIcon } from "@/components/workspace-icon";
import { routes } from "@/constants/url";
import { useUser } from "@/hooks/auth/use-user.ts";
import { setupUserContext } from "@/lib/errors/capture-error";
import { injectParams, useWorkspaceNavigate } from "@/lib/router/use-workspace-navigate";
import { NewPayoutBanner } from "@/routes/payout/components/new-payout-banner";
import {
  Button,
  Thumbnail,
  ThumbnailFallback,
  ThumbnailImage,
  Typography,
  useBottomSheet,
  useModal,
} from "@backpac/monica";
import { useQueryClient } from "@tanstack/react-query";
import { overlay } from "overlay-kit";
import { Suspense, useCallback, useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { Outlet, useLocation } from "react-router";

export function AppWorkspaceLayout() {
  useEffect(() => {
    if (NewPayoutBanner.status.isShowPopup) {
      overlay.open(({ isOpen, close }) => <NewPayoutBanner open={isOpen} onClose={close} />);
    }
  }, []);
  return (
    <GlobalProviders>
      <ErrorBoundary fallbackRender={() => null}>
        <Suspense fallback={null}>
          <AuthCheck />
          <WorkspaceInivitationCheck />
        </Suspense>
      </ErrorBoundary>
      <AppSidebar />
      <main className="h-full min-h-screen w-full bg-backgroundLight">
        <Outlet />
      </main>
    </GlobalProviders>
  );
}

function AuthCheck() {
  const { data: user } = useUser();

  setupUserContext(user);

  return null;
}

function WorkspaceInivitationCheck() {
  const { pathname } = useLocation();
  const {
    data: {
      data: { items: invitations },
    },
  } = useGetInvitationsApiV1Suspense();

  const { open } = useInvitationConfirm();

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    for (const invitation of invitations) {
      open({
        id: invitation.id,
      });
    }
  }, [pathname]);

  return null;
}

function useInvitationConfirm() {
  const queryClient = useQueryClient();
  const navigate = useWorkspaceNavigate();
  const { open: modalOpen, close: modalClose } = useModal();
  const { open: bottomSheetOpen, close: bottomSheetClose } = useBottomSheet();
  const { mutateAsync: acceptInvitation, isPending: isAcceptingInvitation } = useAcceptInvitationApiV1();
  const { mutateAsync: declineInvitation, isPending: isDecliningInvitation } = useDeclineInvitationApiV1();

  const handleAcceptInvitation = useCallback(
    async (invitationId: string, { workspaceId }: { workspaceId: string }) => {
      await acceptInvitation({
        invitationId,
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getGetInvitationsApiV1QueryKey() }),
        queryClient.invalidateQueries({ queryKey: getGetWorkspacesApiV1QueryKey() }),
      ]);

      navigate(injectParams(routes.HOME.root, { workspaceId }));
    },
    [acceptInvitation, queryClient, navigate],
  );

  const handleDeclineInvitation = useCallback(
    async (invitationId: string) => {
      await declineInvitation({ invitationId });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: getGetInvitationsApiV1QueryKey() }),
        queryClient.invalidateQueries({ queryKey: getGetWorkspacesApiV1QueryKey() }),
      ]);
    },
    [declineInvitation, queryClient],
  );

  const open = useCallback(
    (data: { id: string }) => {
      const isMobile = window.innerWidth < 768;

      const desktopContent = (
        <Invitation id={data.id}>
          {(invitation) => (
            <div className="flex flex-col items-center gap-4 pt-10 w-full">
              <Typography font="unbounded" color="textPrimary" size="xl" className="text-center">
                Join the {invitation.workspace.name} workspace
              </Typography>
              <Typography size="paragraphXs" color="textSecondary" className="text-center">
                {invitation.inviterName} ({invitation.inviterEmail}) has invited you to join the{" "}
                <span className="font-bold">{invitation.workspace.name}</span> workspace. Join now to start
                collaborating!
              </Typography>
              <div />
              <div className="flex items-center justify-between p-4 border border-borderLight rounded-[12px] w-full">
                <div className="flex items-center gap-3">
                  <WorkspaceIcon
                    thumbnailUrl={invitation.workspace.thumbnailUrl}
                    workspaceName={invitation.workspace.name}
                    className="size-8"
                  />
                  <Typography size="xs" weight={600}>
                    {invitation.workspace.name}
                  </Typography>
                </div>
                <Typography size="xs" weight={400} color="textSubtle">
                  {invitation.workspaceMemberCount ?? 1} members
                </Typography>
              </div>
              <div />
              <div className="flex gap-2 items-center justify-end self-end">
                <Button
                  variant="ghost"
                  disabled={isDecliningInvitation}
                  size="lg"
                  className="underline underline-offset-2 text-textMuted"
                  onClick={async () => {
                    await handleDeclineInvitation(data.id);
                    modalClose();
                  }}
                >
                  Decline
                </Button>
                <Button
                  variant="black"
                  size="lg"
                  disabled={isAcceptingInvitation}
                  onClick={async () => {
                    await handleAcceptInvitation(data.id, { workspaceId: invitation.workspaceId });
                    modalClose();
                  }}
                >
                  Join
                </Button>
              </div>
            </div>
          )}
        </Invitation>
      );

      const mobileContent = (
        <Invitation id={data.id}>
          {(invitation) => (
            <div className="flex flex-col gap-6 pt-6">
              <div className="flex flex-col items-center gap-2">
                <Typography font="unbounded" color="textPrimary" size="lg" className="text-center">
                  Join the {invitation.workspace.name} workspace
                </Typography>
                <Typography size="paragraphXs" color="textSecondary" className="text-center">
                  Larry ({invitation.email}) has invited you to join the{" "}
                  <span className="font-bold">{invitation.workspace.name}</span> workspace
                </Typography>
              </div>
              <div className="flex items-center justify-between px-4 py-3 border border-borderLight rounded-[8px] ">
                <div className="flex items-center gap-2">
                  <Thumbnail className="size-6" variant="square">
                    <ThumbnailImage
                      src={invitation.workspace.thumbnailUrl ?? ""}
                      alt={`${invitation.workspace.name} workspace logo`}
                      className="size-6 rounded-[4px]"
                    />
                    <ThumbnailFallback>{invitation.workspace.name.slice(0, 1)}</ThumbnailFallback>
                  </Thumbnail>
                  <Typography size="xs" weight={600}>
                    {invitation.workspace.name}
                  </Typography>
                </div>
                <Typography size="xs" weight={400} color="textSubtle">
                  {invitation.workspaceMemberCount ?? 1} members
                </Typography>
              </div>
              <div className="flex flex-col gap-2 pb-4">
                <Button
                  variant="black"
                  size="lg"
                  disabled={isAcceptingInvitation}
                  className="w-full"
                  onClick={async () => {
                    await handleAcceptInvitation(data.id, { workspaceId: invitation.workspaceId });
                    bottomSheetClose();
                  }}
                >
                  Join
                </Button>
                <Button
                  variant="ghost"
                  disabled={isDecliningInvitation}
                  size="lg"
                  className="w-full underline underline-offset-2 text-textMuted"
                  onClick={async () => {
                    await handleDeclineInvitation(data.id);
                    bottomSheetClose();
                  }}
                >
                  Decline
                </Button>
              </div>
            </div>
          )}
        </Invitation>
      );
      if (isMobile) {
        bottomSheetOpen({
          title: "Workspace Invitation",
          withNoHeader: true,
          children: mobileContent,
        });
      } else {
        modalOpen({
          title: "Workspace Invitation",
          className: "max-w-[480px]",
          withNoHeader: true,
          children: desktopContent,
        });
      }
    },
    [
      modalOpen,
      bottomSheetOpen,
      modalClose,
      bottomSheetClose,
      handleAcceptInvitation,
      handleDeclineInvitation,
      isDecliningInvitation,
      isAcceptingInvitation,
    ],
  );

  return { open };
}

function Invitation({ id, children }: { id: string; children: (data: WorkspaceInvitation) => React.ReactNode }) {
  const {
    data: {
      data: { item },
    },
  } = useGetInvitationApiV1Suspense(id);

  return children(item);
}
